import Vue from "vue"
import Vapi from "vuex-rest-api"
import Passenger, { PassengerState } from "../models/Passenger";
import AppEvents from '@/AppEvents';
import TransportationPermission from '../models/TransportationPermission';
import TransportationPermissionInfo from '../models/TransportationPermissionInfo';
import Trip from '../models/Trip';
import IMovitBookingAddressViewModel from '@/components/Booking/models/IMovitBookingAddressViewModel';
import Destination from '../models/Destination';

interface IPassengerState {
    showOnlyActivePassengers: boolean;
    passenger: Passenger | null;
    paginatedPassengers: Passenger[];
    paginatedPassengersTotal: number;
    timetablePassengers: Passenger[];
    arrivalDeparturePassengers: Passenger[];
    transportationPermissionInfos: TransportationPermissionInfo[];
    trips: Trip[];
    passengerPlacesInfo: IMovitBookingAddressViewModel | null;
}

const passengerApi = new Vapi({
    baseURL: process.env.VUE_APP_API_HOST + "/api/passenger",
    state: {
        showOnlyActivePassengers: localStorage.getItem('passengerListFilterInActive') === 'true',
        passenger: null,
        paginatedPassengers: [],
        paginatedPassengersTotal: 0,
        timetablePassengers: [],
        arrivalDeparturePassengers: [],
        transportationPermissionInfos: [],
        trips: [],
        passengerPlacesInfo: null,
    }
})
    .get({
        action: "getPaginatedPassengers",
        property: "paginatedPassengers",
        queryParams: true,
        path: "/paginated",
        onSuccess: (state: IPassengerState, payload, axios) => {
            const passengers = payload.data.passengers.map(s => new Passenger(s));
            state.paginatedPassengers = passengers;
            state.paginatedPassengersTotal = payload.data.total;
        },
        onError: (state: any, error: any, axios: any) => {
            AppEvents.handleApiError(error);
        }
    })
    .post({
        action: "getTimetablePassengers",
        property: "timetablePassengers",
        path: "/byIds",
        onSuccess: (state: IPassengerState, payload, axios) => {
            const passengers = payload.data.map(s => new Passenger(s));
            state.timetablePassengers = passengers.filter(p => p.state === PassengerState.Normal);
        },
        onError: (state: any, error: any, axios: any) => {
            AppEvents.handleApiError(error);
        }
    })
    .post({
        action: "getArrivalDeparturePassengers",
        property: "arrivalDeparturePassengers",
        path: "/byIds",
        onSuccess: (state: IPassengerState, payload, axios) => {
            const passengers = payload.data.map(s => new Passenger(s));
            state.arrivalDeparturePassengers = passengers.filter(p => p.state === PassengerState.Normal);
        },
        onError: (state: any, error: any, axios: any) => {
            AppEvents.handleApiError(error);
        }
    })
    .get({
        action: "search",
        property: "passengers",
        path: ({ search }) => ("/search?q=" + search),
        onSuccess: (state: IPassengerState, payload, axios) => {
            return payload.data;
        },
        onError: (state: any, error: any, axios: any) => {
            AppEvents.handleApiError(error);
        }
    })
    .get({
        action: "getPassengerById",
        property: "passenger",
        path: ({ id }) => ("/" + id),
        onSuccess: (state: IPassengerState, payload, axios) => {
            // console.log("getPassengerById onSuccess", state, payload.data);
            return handlePassengerResponse(state, payload);
        },
        onError: (state: any, error: any, axios: any) => {
            AppEvents.handleApiError(error);
        }
    })
    .post({
        action: "createPassenger",
        property: "passenger",
        path: "/",
        onSuccess: (state: IPassengerState, payload, axios) => {
            // console.log("createPassenger onSuccess", state, payload.data);
            return handlePassengerResponse(state, payload);
        },
        onError: (state: any, error: any, axios: any) => {
            AppEvents.handleApiError(error);
        }
    })
    .put({
        action: "updatePassenger",
        property: "passenger",
        path: ({ id }) => ("/" + id),
        onSuccess: (state: IPassengerState, payload, axios) => {
            // console.log("updatePassenger onSuccess", state, payload.data);
            return handlePassengerResponse(state, payload);
        },
        onError: (state: any, error: any, axios: any) => {
            AppEvents.handleApiError(error);
        }
    })
    .put({
        action: "updatePassengerGroups",
        property: "passenger",
        path: ({ id }) => ("/" + id + "/groups"),
        onSuccess: (state: IPassengerState, payload, axios) => {
            // console.log("updatePassenger onSuccess", state, payload.data);
            // TODO: We should not need to update the local passenger in state.passengers if paginated passengers works everywhere
            return handlePassengerResponse(state, payload);
        },
        onError: (state: any, error: any, axios: any) => {
            AppEvents.handleApiError(error);
        }
    })
    .put({
        action: "deactivate",
        property: "passenger",
        path: ({ id }) => ("/deactivate/" + id),
        onSuccess: (state: IPassengerState, payload, axios) => {
            // console.log("deleterequest onSuccess", state, payload.data);
            return handlePassengerResponse(state, payload);
        },
        onError: (state: any, error: any, axios: any) => {
            AppEvents.handleApiError(error);
        }
    })
    .put({
        action: "activate",
        property: "passenger",
        path: ({ id }) => ("/activate/" + id),
        onSuccess: (state: IPassengerState, payload, axios) => {
            // console.log("deleterequest onSuccess", state, payload.data);
            return handlePassengerResponse(state, payload);

        },
        onError: (state: any, error: any, axios: any) => {
            AppEvents.handleApiError(error);
        }
    })
    .put({
        action: "deleteRequest",
        property: "passenger",
        path: ({ id }) => ("/deleterequest/" + id),
        onSuccess: (state: IPassengerState, payload, axios) => {
            // console.log("deleterequest onSuccess", state, payload.data);
            return handlePassengerResponse(state, payload, true);
        },
        onError: (state: any, error: any, axios: any) => {
            AppEvents.handleApiError(error);
        }
    })
    .put({
        action: "cancelDeleteRequest",
        property: "passenger",
        path: ({ id }) => ("/canceldeleterequest/" + id),
        onSuccess: (state: IPassengerState, payload, axios) => {
            // console.log("canceldeleterequest onSuccess", state, payload.data);
            return handlePassengerResponse(state, payload);
        },
        onError: (state: any, error: any, axios: any) => {
            AppEvents.handleApiError(error);
        }
    })
    .get({
        action: "getPassengerTransportationPermissionInfo",
        property: "getPassengerTransportationPermissionInfo",
        path: ({ passengerId }) => ("/" + passengerId + "/transportationPermissionInfo"),
        onSuccess: (state: IPassengerState, payload, axios) => {
            // console.log("payload data: " + payload.data);
            const transportationPermissionInfo: TransportationPermissionInfo = 
                new TransportationPermissionInfo(payload.data);
            const transportationPermissionIndex = state.transportationPermissionInfos.findIndex(qi => 
                qi.timitPassengerId === transportationPermissionInfo.timitPassengerId
            );
            if (transportationPermissionIndex > -1) {
                // transportationPermissionInfo already exists, update props
                Object.assign(
                    state.transportationPermissionInfos[transportationPermissionIndex],
                    transportationPermissionInfo
                );
            } else {
                // transportationPermissionInfo does not exist, push it
                state.transportationPermissionInfos.push(transportationPermissionInfo);
            }
            return transportationPermissionInfo;
        },
        onError: (state: any, error: any, axios: any) => {
            AppEvents.handleApiError(error);
        }
    })

    .get({
        action: "getPassengerTrips",
        // TODO what is the property used for?
        property: "getPassengerTrips",
        path: ({ passengerId, startDate, endDate, destinationId }) => 
            ("/" + passengerId + "/trips?startDate=" + startDate + 
            "&endDate=" + endDate + "&destinationId=" + destinationId),
        onSuccess: (state: IPassengerState, payload, axios) => {
            const trips = payload.data.map(t => new Trip(t));
            state.trips = trips
            return state.trips;
        },
        onError: (state: any, error: any, axios: any) => {
            console.warn("[Timit] Encountered error when fetching passenger trips");
            AppEvents.handleApiError(error);
        }
    })

    .get({
        action: "getPassengerPlaces",
        // TODO what is the property used for?
        property: "getPassengerPlaces",
        path: ({ passengerId }) => ("/" + passengerId + "/passengerplaces"),
        onSuccess: (state: IPassengerState, payload, axios) => {
            
            // return state.trips;
            // console.log("got response from be: " + payload);
            
            const result =  payload.data as IMovitBookingAddressViewModel;
            // console.log ("allowing free addresses: " + result.allowFreeAddresses);
            // console.log("response has addresses " + result.addresses.length);
            state.passengerPlacesInfo = result;
            return result;
        },
        onError: (state: any, error: any, axios: any) => {
            console.warn("[Timit] Encountered error when fetching passenger places: " + error);
            AppEvents.handleApiError(error);
        }
    })

    .post({
        action: "cancelTrips",
        property: "cancelTrips",
        path: ({passengerId}) => ("/" + passengerId + "/trips/cancel"),
        onSuccess: (state: IPassengerState, payload, axios) => {
            // console.log("succesully called cancel trips");
        },
        onError: (state: any, error: any, axios: any) => {
            // console.log("encountered error when cancelling trips: " + error);
            AppEvents.handleApiError(error);
        }
    })

    .post({
        action: "createTransportationPermission",
        property: "createTransportationPermission",
        path: ({ passengerId, transportationPermissionId }) => ("/" + passengerId + "/transportationPermission"),
        onSuccess: (state: IPassengerState, payload, axios) => {
            const transportationPermission: TransportationPermission = new TransportationPermission(payload.data);
            const transportationPermissionInfo: TransportationPermissionInfo = state.transportationPermissionInfos
            .find(qi => 
                qi.timitPassengerId === transportationPermission.timitPassengerId && 
                qi.movitInstanceId === transportationPermission.movitInstanceId
            )
            const transportationPermissionIndex = transportationPermissionInfo.permissions.findIndex(
                q => 
                    q.id === transportationPermission.id 
                    // && 
                    // q.movitInstanceId === transportationPermission.movitInstanceId
            );
            if (transportationPermissionIndex > -1) {
                // transportationPermission already exists, update props
                Object.assign(
                    transportationPermissionInfo.permissions[transportationPermissionIndex],
                    transportationPermission);
            } else {
                // transportationPermission does not exist, push it
                transportationPermissionInfo.permissions.push(transportationPermission);
            }
            return transportationPermission;
        },
        onError: (state: any, error: any, axios: any) => {
            AppEvents.handleApiError(error);
        }
    })
    .put({
        action: "updateTransportationPermission",
        property: "updateTransportationPermission",
        path: ({ passengerId, transportationPermissionId }) => 
            ("/" + passengerId + "/transportationPermission/" + transportationPermissionId),
        onSuccess: (state: IPassengerState, payload, axios) => {
            const transportationPermission: TransportationPermission = new TransportationPermission(payload.data);
            const transportationPermissionInfo: TransportationPermissionInfo = state.transportationPermissionInfos
            .find(qi => 
                qi.timitPassengerId === transportationPermission.timitPassengerId
                //  && 
                // qi.movitInstanceId === transportationPermission.movitInstanceId
            )
            const transportationPermissionIndex = transportationPermissionInfo.permissions.findIndex(q => 
                q.id === transportationPermission.id 
                // && 
                // q.movitInstanceId === transportationPermission.movitInstanceId
            );
            if (transportationPermissionIndex > -1) {
                // transportationPermission already exists, update props
                console.log(transportationPermission)
                Object.assign(
                    transportationPermissionInfo.permissions[transportationPermissionIndex],
                    transportationPermission);
            } else {
                // transportationPermission does not exist, push it
                transportationPermissionInfo.permissions.push(transportationPermission);
            }
            return transportationPermission;
        },
        onError: (state: any, error: any, axios: any) => {
            AppEvents.handleApiError(error);
        }
    })
    .delete({
        action: "deleteTransportationPermission",
        property: "deleteTransportationPermission",
        path: ({ passengerId, transportationPermissionId }) => 
            ("/" + passengerId + "/transportationPermission/" + transportationPermissionId),
        onSuccess: (state: IPassengerState, payload, axios) => {
            const transportationPermission: TransportationPermission = new TransportationPermission(payload.data);
            const transportationPermissionInfo: TransportationPermissionInfo = state.transportationPermissionInfos
            .find(qi => 
                qi.timitPassengerId === transportationPermission.timitPassengerId
                //  && 
                // qi.movitInstanceId === transportationPermission.movitInstanceId
            )
            const transportationPermissionIndex = transportationPermissionInfo.permissions.findIndex(q => 
                q.id === transportationPermission.id && 
                q.movitInstanceId === transportationPermission.movitInstanceId
            );
            if (transportationPermissionIndex > -1) {
               // transportationPermission exists, delete its
               transportationPermissionInfo.permissions.splice(transportationPermissionIndex, 1);
            } else {
                // do nothing
            }
            return transportationPermission;
        },
        onError: (state: any, error: any, axios: any) => {
            AppEvents.handleApiError(error);
        }
    })

const getters = {
    passengerOrganizationId: (state: IPassengerState, getters: any, rootState: any, rootGetters: any) => (passengerId: string, destinationIds: number[] = []) => {
        const id = parseInt(passengerId, 10);
        if (isNaN(id)) {
            return null;
        }

        if (id == 0) {
            const destinationId = destinationIds.find(x => x);
            if (!destinationId) {
                return null;
            }
            const destination: Destination = rootState.destination.destinations.find(d => d.id === destinationId);
            if (!destination) {
                return null;
            }

            return destination.organizationId || null;
        }

        const passenger = rootGetters["passenger/findPassengerById"](id);
        if (passenger) {
            return passenger.organizationId;
        }
        return null;
    },
    findPassengerById: (state: IPassengerState, getters: any, rootState: any) => (passengerId: string) => {
        const id = parseInt(passengerId, 10);
        if (isNaN(id)) {
            return null;
        }
        const collections = [
            state.paginatedPassengers,
            state.passenger ? [state.passenger] : []
        ]
        for(const collection of collections){
            const passenger = collection.find(p => p.id === id);
            if (passenger){
                return passenger;
            }
        }
        return null;
    }
}

const handlePassengerResponse = (state: IPassengerState, payload: any, deleteLocal: boolean = false) => {
    const passenger = new Passenger(payload.data);
    if (deleteLocal){
        state.passenger = null;
        return passenger;
    }
    if (state.passenger && state.passenger.id === passenger.id) {
        // passenger already exists, update props
        Object.assign(state.passenger, passenger);
    } else {
        // passenger does not exist, push it
        state.passenger = passenger;
    }
    return passenger;
}

const passengerStore = passengerApi.getStore();
(passengerStore as any).namespaced = true;
(passengerStore as any).getters = getters;

passengerStore.actions.addPassenger = (context, value: Passenger) => {
    context.commit("addPassenger", value)
}

passengerStore.actions.replacePassenger = (context, value: Passenger) => {
    context.commit("replacePassenger", value);
}

passengerStore.mutations.addPassenger = (state, value: Passenger) => {
    state.passengers.push(value);
    console.log({ addPassengerCommit: value, foundPassenger: state.passengers.find(p => p.id === value.id) });
}

passengerStore.mutations.replacePassenger = (state, value: Passenger) => {
    const passengerIndex = state.passengers.findIndex(p => p.id === value.id);
    if (passengerIndex > -1) {
        state.passengers.splice(passengerIndex, 1, value);
    }
}

passengerStore.mutations.changeShowActivePassengers = (state, value: boolean) => {
    localStorage.setItem('passengerListFilterInActive', value ? 'true' : 'false');
    state.showOnlyActivePassengers = value;
}

passengerStore.mutations.setHasSsnToPassenger = (state: IPassengerState, { passengerId, ssn }: { passengerId: number, ssn: string | null }) => {
    if (state.passenger && state.passenger.id === passengerId) {
        console.log({HasSsn: !!ssn });
        state.passenger.hasSsn = !!ssn
    }
}

passengerStore.mutations.resetSelectedPassenger = (state) => {
    state.passenger = null;
}

export default passengerStore;
